import React, { useCallback } from 'react';
import { Alert, Stack } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { FormattedMessage, useIntl } from 'react-intl';

import { CREATE_CONTACT_MUTATION, GET_MY_CONTACTS_QUERY } from '../../gql';
import {
  Button,
  FieldLabel,
  FormField,
  Paper,
  SurfaceTextField,
} from '../design-system';

interface CreateContactCardProps {
  onClose?: () => void;
}

interface ContactRequestFormValues {
  email: string;
}

const formStyles = { maxWidth: 520 };
const actionsStyles = { justifyContent: 'flex-end' };

const CreateContactCard = ({ onClose }: CreateContactCardProps) => {
  const intl = useIntl();
  const {
    reset,
    control,
    handleSubmit,
    formState: { isDirty, isValid },
  } = useForm<ContactRequestFormValues>({
    defaultValues: { email: '' },
    mode: 'onChange',
  });
  const [create, { called, loading }] = useMutation(CREATE_CONTACT_MUTATION, {
    refetchQueries: [{ query: GET_MY_CONTACTS_QUERY }],
  });
  const onSubmit = useCallback(
    async (data: ContactRequestFormValues) => {
      await create({ variables: { email: data.email } });
      reset({ email: '' });
    },
    [create, reset]
  );

  return (
    <Paper component="form" onSubmit={handleSubmit(onSubmit)} sx={formStyles}>
      <Stack gap={2}>
        <FormField>
          <FieldLabel htmlFor="contact-request-email">
            <FormattedMessage
              id="contacts.request.send"
              defaultMessage="Send contact request"
            />
          </FieldLabel>
          <Controller
            name="email"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <SurfaceTextField
                {...field}
                autoFocus
                id="contact-request-email"
                placeholder={intl.formatMessage({
                  id: 'account.email',
                  defaultMessage: 'Email',
                })}
                surface="sunken"
                type="email"
              />
            )}
          />
        </FormField>
        {called && !isDirty && (
          <Alert severity="success">
            <FormattedMessage
              id="contacts.request.sentSuccess"
              defaultMessage="Contact request sent"
            />
          </Alert>
        )}
        <Stack direction="row" gap={1} sx={actionsStyles}>
          {onClose && (
            <Button onClick={onClose} tone="neutral" type="button">
              <FormattedMessage id="general.cancel" defaultMessage="Cancel" />
            </Button>
          )}
          <Button disabled={loading || !isDirty || !isValid} type="submit">
            <FormattedMessage
              id="contacts.request.sendAction"
              defaultMessage="Send request"
            />
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};

export { CreateContactCard };
